import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import OfferCard from "../components/OfferCard";
import ImageUploader from "../components/ImageUploader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Rocket, Loader2, CalendarClock } from "lucide-react";
import { toast } from "sonner";

export default function Boost() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const load = () => api.get("/me/offers").then((r) => { setData(r.data); setFailed(false); }).catch(() => setFailed(true));
  useEffect(() => { if (user && user.role !== "admin") load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (user?.role === "admin") return <Navigate to="/offers" replace />;
  if (failed) {
    return (
      <div className="py-24 text-center" data-testid="boost-error">
        <p className="font-display text-xl font-bold">Couldn&apos;t load your offers</p>
        <Button variant="outline" className="mt-4" onClick={load} data-testid="boost-retry">Try again</Button>
      </div>
    );
  }
  if (!data) return <div className="py-24 flex justify-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const { eligibility: e, offers } = data;
  const live = offers.find((o) => !o.removed && new Date(o.expires_at) > new Date());

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="boost-page">
      <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2"><Rocket className="w-3 h-3" /> Boost your business</p>
      <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter">Post an offer.</h1>
      <p className="text-zinc-600 mt-3 max-w-2xl">
        Your offer appears on <Link to="/offers" className="underline">Today&apos;s offers</Link> and as a badge on your listing for 24 hours.
        One offer every 30 days · max {e.cap} businesses live at once.
      </p>
      <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-zinc-200 text-sm font-mono" data-testid="boost-slots">
        {e.slots_left} of {e.cap} slots free right now
      </div>

      {live && (
        <div className="mt-8 max-w-md" data-testid="boost-live-offer">
          <p className="font-display text-xl font-bold mb-3">Your live offer</p>
          <OfferCard offer={live} />
        </div>
      )}

      {e.can_post ? <OfferForm onPosted={load} /> : (
        <div className="mt-8 p-5 rounded-lg border border-zinc-200 bg-zinc-50 flex gap-3" data-testid="boost-blocked">
          <CalendarClock className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold" data-testid="boost-blocked-reason">{e.reason}</p>
            {e.next_allowed_at && <p className="text-sm text-zinc-600 mt-1" data-testid="boost-next-date">Next offer available {new Date(e.next_allowed_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}.</p>}
            {!e.next_allowed_at && e.reason?.includes("live listing") && <Link to="/dashboard" className="text-sm underline mt-1 inline-block">Go to your listing</Link>}
          </div>
        </div>
      )}
    </div>
  );
}

function OfferForm({ onPosted }) {
  const [f, setF] = useState({ title: "", description: "", label: "", photos: [] });
  const [saving, setSaving] = useState(false);
  const set = (k) => (ev) => setF((s) => ({ ...s, [k]: ev.target.value }));

  const submit = async (ev) => {
    ev.preventDefault();
    setSaving(true);
    try {
      await api.post("/me/offers", { title: f.title, description: f.description, label: f.label || null, photo: f.photos[0] || null });
      toast.success("Your offer is live for 24 hours");
      onPosted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="mt-8 bg-white border border-zinc-200 rounded-lg p-6 md:p-8 space-y-5 max-w-2xl" data-testid="offer-form">
      <div className="grid md:grid-cols-[1fr_180px] gap-4">
        <div>
          <Label htmlFor="of-title">Offer title</Label>
          <Input id="of-title" required maxLength={80} value={f.title} onChange={set("title")} className="mt-1" placeholder="e.g. Free quote + 10% off garden clearances" data-testid="offer-title" />
        </div>
        <div>
          <Label htmlFor="of-label">Discount or price (optional)</Label>
          <Input id="of-label" maxLength={24} value={f.label} onChange={set("label")} className="mt-1" placeholder="20% off" data-testid="offer-label-input" />
        </div>
      </div>
      <div>
        <Label htmlFor="of-desc">Short description</Label>
        <Textarea id="of-desc" required maxLength={500} rows={3} value={f.description} onChange={set("description")} className="mt-1" data-testid="offer-description" />
        <p className="text-xs text-zinc-400 mt-1 text-right">{f.description.length}/500</p>
      </div>
      <div>
        <Label className="mb-2 block">Photo (optional)</Label>
        <ImageUploader value={f.photos} onChange={(photos) => setF((s) => ({ ...s, photos }))} maxFiles={1} />
      </div>
      <Button type="submit" disabled={saving} className="bg-[#FFCC00] hover:bg-[#E6B800] text-black font-semibold" data-testid="offer-submit">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Rocket className="w-4 h-4 mr-2" /> Post offer for 24 hours</>}
      </Button>
      <p className="text-xs text-zinc-500">Once posted, you can&apos;t post another offer for 30 days.</p>
    </form>
  );
}
